import { z } from "zod";

/** Browser notifications (round one, the founder's Go of 2026-10-18; brief `docs/briefs/browser-notifications.md`): the pure rules the API,
 *  the notifier and the web app share. A browser's subscription is the address of its push service (Google's, Mozilla's, Apple's or
 *  Microsoft's) and two keys; only those four services are accepted (the review's finding S1), at most 20 browsers per person and workspace. */

export const PUSH_MAX_BROWSERS = 20;
/** the stored browser description (the request's user agent) is cut to this */
export const PUSH_USER_AGENT_MAX = 300;
/** the push services a browser's address may point at: the four browser makers' own (exact host, or any host under the domain) */
const PUSH_HOSTS: { exact?: string; under?: string }[] = [
  { exact: "fcm.googleapis.com" }, // Chrome, Edge on Android, Opera
  { exact: "updates.push.services.mozilla.com" }, // Firefox
  { under: "push.apple.com" }, // Safari (web.push.apple.com)
  { under: "notify.windows.com" }, // Edge on Windows (wns2-*.notify.windows.com)
];

/** is this a push service's own https address (never http, an IP, an internal name, a port or a look-alike host)? */
export function isPushServiceAddress(endpoint: string): boolean {
  let url: URL;
  try { url = new URL(endpoint); } catch { return false; }
  if (url.protocol !== "https:" || url.port !== "" || url.username || url.password) return false;
  const host = url.hostname.toLowerCase();
  return PUSH_HOSTS.some((h) => (h.exact ? host === h.exact : host.endsWith(`.${h.under}`)));
}

/** the number of bytes a base64url text decodes to, or -1 when it is not base64url */
function base64urlBytes(s: string): number {
  if (!/^[A-Za-z0-9_-]+={0,2}$/.test(s)) return -1;
  const bare = s.replace(/=+$/, "");
  if (bare.length % 4 === 1) return -1;
  return Math.floor((bare.length * 3) / 4);
}
/** a browser's public key: an uncompressed P-256 point (65 bytes, first byte 4, so its text starts with "B") */
const isBrowserKey = (s: string) => base64urlBytes(s) === 65 && s.startsWith("B");
/** the browser's secret: 16 bytes */
const isAuthSecret = (s: string) => base64urlBytes(s) === 16;

/** what `PushSubscription.toJSON()` hands the server, checked: a push service's address and a real browser's keys */
export const PushSubscriptionInputSchema = z.object({
  endpoint: z.string().max(1000).refine(isPushServiceAddress, { message: "Not a browser push service's address" }),
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: z.string().refine(isBrowserKey, { message: "Not a browser's public key" }),
    auth: z.string().refine(isAuthSecret, { message: "Not a browser's secret" }),
  }),
  /** the device notifications round (2026-11-01): the page's own word for a device its browser description hides; only "ipad" is known (an
   *  iPad asks for the desktop site and describes itself as a Mac), anything else is ignored */
  device: z.unknown().optional().transform((v): PushDeviceMark | undefined => (v === "ipad" ? "ipad" : undefined)),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionInputSchema>;

/** one browser as its owner's list shows it: never the address, never the keys */
export type PushBrowser = { id: string; browser: string; createdAt: string; lastSuccessAt: string | null; disabledReason: string | null };
export type PushListing = { publicKey: string | null; subscriptions: PushBrowser[] };

/** a device the page names that the browser description cannot tell (the device notifications round, 2026-11-01) */
export type PushDeviceMark = "ipad";
/** how the mark is kept: in front of the stored browser description, inside its 300 characters (no new column) */
const IPAD_MARK = "[ipad] ";
/** the description to store: the request's user agent, with the page's iPad mark in front, cut to the limit */
export function markedUserAgent(userAgent: string | null | undefined, device: PushDeviceMark | undefined): string | null {
  if (!userAgent && !device) return null;
  const marked = `${device === "ipad" ? IPAD_MARK : ""}${userAgent ?? ""}`;
  return marked.slice(0, PUSH_USER_AGENT_MAX);
}

const BROWSERS: [RegExp, string][] = [[/\bEdg(e|A|iOS)?\//, "Edge"], [/\bOPR\//, "Opera"], [/\b(Firefox|FxiOS)\//, "Firefox"], [/\b(Chrome|CriOS|Chromium)\//, "Chrome"], [/\bVersion\/[\d.]+.*\bSafari\//, "Safari"]];
const SYSTEMS: [RegExp, string][] = [[/\bAndroid\b/, "Android"], [/\bCrOS\b/, "ChromeOS"], [/\b(Macintosh|Mac OS X)\b/, "a Mac"], [/\bWindows\b/, "Windows"], [/\bLinux\b/, "Linux"]];
/** the browser named in a description; WebKit on a Mac without any browser's token is Safari's own (its web apps, "Add to Dock") */
function browserOf(ua: string): string | undefined {
  const named = BROWSERS.find(([re]) => re.test(ua))?.[1];
  if (named) return named;
  return /\bMacintosh\b/.test(ua) && /\bAppleWebKit\//.test(ua) ? "Safari" : undefined;
}
/** a device in words for the person's list and the back office (the device notifications round, 2026-11-01): "Chrome on a Mac", "Edge on
 *  Windows", "Chrome on Android"; an iPhone or iPad first, whatever browser it names, as the Home Screen app every iOS subscription is; a
 *  string nobody knows reads "A device" */
export function deviceName(userAgent: string | null | undefined): string {
  const ua = userAgent ?? "";
  if (ua.startsWith(IPAD_MARK) || /\biPad\b/.test(ua)) return "iPad · Home Screen app";
  if (/\b(iPhone|iPod)\b/.test(ua)) return "iPhone · Home Screen app";
  const browser = browserOf(ua);
  if (!browser) return "A device";
  const system = SYSTEMS.find(([re]) => re.test(ua))?.[1];
  return system ? `${browser} on ${system}` : browser;
}
